import api from "./axios";

export const sendCouncilChat = ({ message, conversationId = null, debate = false }) =>
  api.post("/api/council/chat/", {
    message,
    conversation_id: conversationId,
    debate,
  });

export const listConversations = () => api.get("/api/council/conversations/");

export const getConversation = (conversationId) =>
  api.get(`/api/council/conversations/${conversationId}/`);

export const deleteConversation = (conversationId) =>
  api.delete(`/api/council/conversations/${conversationId}/`);

/**
 * Streaming Council chat over Server-Sent Events. The backend emits
 * `agent_start`, `tool`, `delta`, `final`, and `error` events; this helper
 * normalises them into onEvent callbacks and resolves the returned promise
 * with the final payload when the stream completes.
 *
 * Implemented with fetch + ReadableStream (not native EventSource) so we
 * can set the Authorization header instead of leaking the JWT into a URL
 * query param. Falls back gracefully — any network or parse error is
 * surfaced to the caller so they can swap to the non-streaming endpoint.
 */
export const streamCouncilChat = async ({
  message,
  conversationId = null,
  debate = false,
  onEvent,
  signal,
}) => {
  const baseURL = api.defaults.baseURL;
  const token = localStorage.getItem("access") || "";
  const url = new URL(baseURL + "/api/council/chat/stream/");
  url.searchParams.set("message", message);
  if (conversationId) url.searchParams.set("conversation_id", conversationId);
  // Force the multi-round "fight" (otherwise the backend decides via smart-auto).
  if (debate) url.searchParams.set("debate", "1");

  const resp = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "text/event-stream",
      Authorization: token ? `Bearer ${token}` : "",
    },
    signal,
  });
  if (!resp.ok || !resp.body) {
    // Surface the status + JSON body (e.g. a 402 upgrade_required) so callers
    // can react instead of seeing a generic failure.
    let data = null;
    try {
      data = await resp.json();
    } catch {
      /* not JSON */
    }
    const err = new Error(`Stream failed: HTTP ${resp.status}`);
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalPayload = null;

  // Parse the SSE protocol: events are separated by blank lines; each event
  // has `event: <type>\n` and `data: <json>\n` lines.
  const flushFrame = (raw) => {
    let eventType = "message";
    const dataLines = [];
    for (const line of raw.split("\n")) {
      if (line.startsWith("event:")) eventType = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length === 0) return;
    let payload;
    try {
      payload = JSON.parse(dataLines.join("\n"));
    } catch (err) {
      return;
    }
    if (eventType === "final") finalPayload = payload;
    if (onEvent) onEvent(eventType, payload);
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      if (frame.trim()) flushFrame(frame);
    }
  }
  if (buffer.trim()) flushFrame(buffer);

  if (!finalPayload) {
    throw new Error("Stream ended without a final event");
  }
  return finalPayload;
};
